import React, { useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import atlassianChampionsLogo from '../../assets/images/champions/atlassian-champions-logo.png';
import {
  validateChampionConsentForm,
  ChampionConsentFormValues,
  ChampionConsentFormErrors,
} from '../../utils/ChampionConsentFormValidation';

const PRIVACY_POLICY_URL = 'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers';
const NDA_URL =
  'https://atlassian--uat.sandbox.my.site.com/atlassianchampions/resource/1743746183000/ChampionsNDA';

type ChampionConsentFormProps = {
  firstName: string;
  lastName: string;
  emailId: string;
};

const ChampionConsentForm: React.FC<ChampionConsentFormProps> = ({
  firstName: initialFirstName,
  lastName: initialLastName,
  emailId,
}) => {
  const [values, setValues] = useState<ChampionConsentFormValues>({
    firstName: initialFirstName,
    lastName: initialLastName,
    email: emailId,
    agreePrivacy: false,
    agreeNda: false,
  });
  const [errors, setErrors] = useState<ChampionConsentFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const updateField = <K extends keyof ChampionConsentFormValues>(
    key: K,
    value: ChampionConsentFormValues[K]
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateChampionConsentForm(values);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    // TODO: Implement consent submission
    setIsSubmitting(false);
    setSubmitStatus({ type: 'success', message: 'Consent submitted successfully.' });
  };

  return (
    <div className="champion-form" css={outerStyles}>
      <div css={formColumnStyles}>
        <div css={logoContainerStyles}>
          <div css={logoCenterStyles}>
            <img src={atlassianChampionsLogo} alt="Atlassian Champions" css={logoStyles} />
          </div>
        </div>
        <div css={innerStyles}>
          <form css={formStyles} onSubmit={handleSubmit}>
            <h1 css={headingLargeStyles}>Champion Consent</h1>

            <p css={[introTextStyles, marginBottomXSmallStyles]}>
              Questions marked with an asterisk<abbr css={introAsteriskStyles} title="required">*</abbr> are required.
            </p>
            <p css={[introTextStyles, marginBottomMediumStyles]}>
              Issues with this form? We&rsquo;re here to help. Email us at communitymanagers@atlassian.com
            </p>

            <div css={sectionBoxStyles}>
              <div css={fieldStyles}>
                <label css={[labelStyles, salesforceLabelStyles]} htmlFor="consent-first-name">
                  <abbr css={requiredStyles} title="required" aria-hidden="true">
                    *
                  </abbr>
                  First name
                </label>
                <Textfield
                  id="consent-first-name"
                  value={values.firstName}
                  onChange={(e) => updateField('firstName', (e.target as HTMLInputElement).value)}
                  isInvalid={!!errors?.firstName}
                  isCompact
                />
                {errors?.firstName && <div css={errorStyles}>{errors.firstName}</div>}
              </div>

              <div css={fieldTopStyles}>
                <label css={[labelStyles, salesforceLabelStyles]} htmlFor="consent-last-name">
                  <abbr css={requiredStyles} title="required" aria-hidden="true">
                    *
                  </abbr>
                  Last name
                </label>
                <Textfield
                  id="consent-last-name"
                  value={values.lastName}
                  onChange={(e) => updateField('lastName', (e.target as HTMLInputElement).value)}
                  isInvalid={!!errors?.lastName}
                  isCompact
                />
                {errors?.lastName && <div css={errorStyles}>{errors.lastName}</div>}
              </div>

              <div css={fieldTopStyles}>
                <label css={[labelStyles, salesforceLabelStyles]} htmlFor="consent-email">
                  <abbr css={requiredStyles} title="required" aria-hidden="true">
                    *
                  </abbr>
                  Contact email
                </label>
                <Textfield
                  id="consent-email"
                  type="email"
                  value={values.email}
                  isDisabled
                  isCompact
                />
              </div>
            </div>

            <div css={sectionBoxStyles}>
              <div css={controlTopStyles}>
                <div
                  css={agreementRowStyles}
                  className={
                    errors?.agreePrivacy
                      ? 'champion-form-agreements champion-form-agreements-privacy--error'
                      : 'champion-form-agreements'
                  }
                >
                  <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
                    <abbr css={requiredStyles} title="required" aria-hidden="true">
                      *
                    </abbr>
                    <Checkbox
                      name="consent_agree_privacy"
                      label={
                        <>
                          By submitting this form I agree to{' '}
                          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" css={inlineLinkStyles}>
                            Atlassian Privacy Policy
                          </a>
                        </>
                      }
                      isChecked={values.agreePrivacy}
                      onChange={() => updateField('agreePrivacy', !values.agreePrivacy)}
                    />
                  </div>
                </div>

                <div
                  css={agreementRowTopStyles}
                  className={
                    errors?.agreeNda
                      ? 'champion-form-agreements champion-form-agreements-nda--error'
                      : 'champion-form-agreements'
                  }
                >
                  <div css={checkboxRowStyles} className="champion-form-agreements-checkbox-row">
                    <abbr css={requiredStyles} title="required" aria-hidden="true">
                      *
                    </abbr>
                    <Checkbox
                      name="consent_agree_nda"
                      label={
                        <>
                          By checking this box, I acknowledge and agree that I am electronically signing the{' '}
                          <a href={NDA_URL} target="_blank" rel="noopener noreferrer" css={inlineLinkStyles}>
                            Non-Disclosure Agreement (NDA)
                          </a>
                          {' '}and that this electronic signature is the legal equivalent of my manual signature on the NDA. NDAs allow Atlassian Champions to receive early product news and access to feedback opportunities.
                        </>
                      }
                      isChecked={values.agreeNda}
                      onChange={() => updateField('agreeNda', !values.agreeNda)}
                    />
                  </div>
                </div>
              </div>

              {(errors?.agreePrivacy || errors?.agreeNda) && (
                <div css={errorStyles}>
                  {errors.agreePrivacy || errors.agreeNda}
                </div>
              )}
            </div>

            {submitStatus?.type === 'success' && (
              <div css={successBannerStyles}>{submitStatus.message}</div>
            )}
            {submitStatus?.type === 'error' && (
              <div css={errorBannerStyles}>{submitStatus.message}</div>
            )}

            <div css={marginTopLargeStyles} className="champion-form-submit">
              <Button
                type="submit"
                appearance="primary"
                isDisabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting\u2026' : 'Submit'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChampionConsentForm;

const outerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
  zIndex: 0,
  overflow: 'visible',
  padding: token('space.600'),
  paddingTop: token('space.075'),
  '& [data-ds--text-field--container]': {
    height: '2rem',
    minHeight: '2rem',
    maxHeight: '2rem',
    padding: 0,
    borderRadius: '0.25rem',
    borderColor: '#c9c9c9',
    backgroundColor: '#fff',
  },
  '& [data-ds--text-field--container] input': {
    fontSize: '0.8125rem',
    padding: '0 0.75rem',
    height: '100%',
  },
});

const formColumnStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
});

const logoContainerStyles = css({
  width: '100%',
  maxWidth: '686px',
  marginBottom: '12px',
});

const logoCenterStyles = css({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
});

const logoStyles = css({
  maxWidth: '686px',
  width: '100%',
  height: 'auto',
});

const innerStyles = css({
  width: '100%',
  maxWidth: '600px',
});

const formStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.0'),
});

const headingLargeStyles = css({
  fontSize: '22.4pt',
  fontWeight: 600,
  fontFamily: 'sans-serif',
  marginBottom: token('space.300'),
});

const introTextStyles = css({
  fontSize: '10pt',
  fontFamily: 'sans-serif',
});

const marginBottomXSmallStyles = css({
  marginBottom: token('space.100'),
});

const marginBottomMediumStyles = css({
  marginBottom: token('space.200'),
});

const marginTopLargeStyles = css({
  marginTop: token('space.200'),
});

const introAsteriskStyles = css({
  color: 'inherit',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const sectionBoxStyles = css({
  marginBottom: '1rem',
});

const fieldStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const fieldTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: '1rem',
});

const labelStyles = css({
  fontWeight: 400,
  fontSize: '0.875rem',
  color: '#3e3e3c',
  marginBottom: token('space.050'),
  lineHeight: 1.5,
});

const salesforceLabelStyles = css({
  color: '#696969',
  fontSize: '0.75rem',
  fontFamily: 'sans-serif',
  fontWeight: 400,
  marginBottom: '0.125rem',
  paddingRight: '0.5rem',
  paddingTop: '0.25rem',
  display: 'inline-block',
  overflowWrap: 'break-word',
  wordWrap: 'break-word',
  hyphens: 'auto',
});

const requiredStyles = css({
  color: 'var(--champion-form-error)',
  textDecoration: 'none',
  marginRight: '0.125rem',
});

const errorStyles = css({
  color: 'var(--champion-form-error)',
  fontFamily: 'sans-serif',
  fontSize: '0.75rem',
  fontWeight: 400,
  marginTop: token('space.050'),
});

const controlTopStyles = css({
  marginTop: token('space.050'),
});

const checkboxRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '0.25rem',
});

const agreementRowStyles = css({
  display: 'flex',
  flexDirection: 'column',
});

const agreementRowTopStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginTop: token('space.050'),
});

const inlineLinkStyles = css({
  color: 'var(--champion-form-blue)',
  textDecoration: 'underline',
});

const successBannerStyles = css({
  padding: token('space.200'),
  borderRadius: '3px',
  backgroundColor: token('color.background.success'),
  color: token('color.text.success'),
  fontWeight: 600,
});

const errorBannerStyles = css({
  padding: token('space.200'),
  borderRadius: '0.25rem',
  backgroundColor: '#fef1ee',
  color: 'var(--champion-form-error)',
  fontWeight: 400,
});
